export class ApiError extends Error {
  public constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const SESSION_EXPIRED_EVENT = 'financego:session-expired';

export async function apiRequest<T>(
  path: string,
  init: RequestInit = {},
  retrySession = true,
): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: {
      ...(init.body === undefined ? {} : { 'content-type': 'application/json' }),
      ...init.headers,
    },
  });

  if (response.status === 401 && retrySession && path !== '/api/users/refresh') {
    const refresh = await fetch('/api/users/refresh', {
      credentials: 'include',
      method: 'POST',
    });
    if (refresh.ok) return apiRequest<T>(path, init, false);
    if (typeof window !== 'undefined') window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
  }

  if (!response.ok) throw await toApiError(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export function jsonBody(value: unknown): string {
  return JSON.stringify(value);
}

async function toApiError(response: Response): Promise<ApiError> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    body = null;
  }
  const code = getString(body, 'code') ?? 'REQUEST_ERROR';
  const message = getString(body, 'error') ?? 'Request failed';
  return new ApiError(response.status, code, message);
}

function getString(value: unknown, key: string): string | null {
  if (typeof value !== 'object' || value === null || !(key in value)) return null;
  const field = (value as Record<string, unknown>)[key];
  return typeof field === 'string' ? field : null;
}
